import { useEffect, useState, type ReactElement } from 'react'
import { AnimatePresence } from 'framer-motion'
import { Sparkle, ELEMENT_INFO, ANIMAL_INFO } from '../design-system'
import type { Animal, Element } from '@origami/shared'
import { useTitleHandlers } from './useTitleHandlers'
import { RotatePrompt, useIsMobile, useIsFoldOpen } from './useIsMobile'
import { TitleToast } from './TitleToast'
import { MultiTwinScrolls } from './MultiTwinScrolls'
import './TitleScreenB.css'

const ANIMALS: Animal[]   = ['bird', 'mammal', 'reptile', 'fish', 'insect']
const ELEMENTS: Element[] = ['fire', 'water', 'wind', 'wood', 'earth']

// Star field positions in the painted night sky. Keep numbering
// stable so we can refer to a specific star by index when nudging.
interface Star { top: string; left: string; size: number; color: string; dur: string; delay: string }
const STARS: Star[] = [
  { top: '4%',  left: '1%',  size: 16, color: '#ffd166', dur: '4.5s', delay: '0s'    }, //  1
  { top: '6%',  left: '19%', size: 12, color: '#ffe28a', dur: '3.8s', delay: '-1s'   }, //  2
  { top: '9%',  left: '7%',  size: 20, color: '#ffb866', dur: '5.2s', delay: '-2s'   }, //  3
  { top: '18%', left: '28%', size: 10, color: '#fff2c0', dur: '3.4s', delay: '-0.6s' }, //  4
  { top: '5%',  left: '27%', size: 14, color: '#ffe28a', dur: '4.2s', delay: '-1.8s' }, //  5
  { top: '23%', left: '42%', size: 10, color: '#ffd166', dur: '4.0s', delay: '-2.6s' }, //  6
  { top: '2%',  left: '42%', size: 10, color: '#fff2c0', dur: '3.6s', delay: '-1.4s' }, //  7
  { top: '3%',  left: '58%', size: 14, color: '#ffe28a', dur: '4.6s', delay: '-2.2s' }, //  8
  { top: '3%',  left: '78%', size: 12, color: '#fff2c0', dur: '5.6s', delay: '-3s'   }, //  9
  { top: '3%',  left: '96%', size: 18, color: '#ffd166', dur: '4.0s', delay: '-2.4s' }, // 10
  { top: '5%',  left: '86%', size: 14, color: '#ffb866', dur: '4.8s', delay: '-1.2s' }, // 11
  { top: '21%', left: '70%', size: 10, color: '#ffe28a', dur: '3.6s', delay: '-0.5s' }, // 12
  { top: '8%',  left: '65%', size: 16, color: '#fff2c0', dur: '5.4s', delay: '-3.4s' }, // 13
  { top: '18%', left: '87%', size: 12, color: '#ffd166', dur: '4.4s', delay: '-2.8s' }, // 14
]

const MENU = [
  { id: 'solo'  as const, label: 'Begin Solo Run',  sub: 'play a quick draft' },
  { id: 'lobby' as const, label: 'Make a Lobby',    sub: 'fold with friends' },
  { id: 'join'  as const, label: 'Join with Code',  sub: 'enter a 6-letter scroll' },
  { id: 'codex' as const, label: 'Compendium',      sub: 'leaf the bestiary' },
]

// Mobile landscape star field — distributed in the upper sky band
// of an 844×390-ish viewport. Avoid the centered title-block area.
const STARS_LANDSCAPE: Star[] = [
  { top: '6%',  left: '4%',  size: 14, color: '#ffd166', dur: '4.5s', delay: '0s'    },
  { top: '13%', left: '11%', size: 10, color: '#ffe28a', dur: '3.8s', delay: '-1s'   },
  { top: '20%', left: '6%',  size: 16, color: '#ffb866', dur: '5.2s', delay: '-2s'   },
  { top: '5%',  left: '21%', size: 12, color: '#fff2c0', dur: '4.6s', delay: '-2.2s' },
  { top: '10%', left: '83%', size: 14, color: '#ffd166', dur: '4.0s', delay: '-2.4s' },
  { top: '17%', left: '92%', size: 10, color: '#ffe28a', dur: '4.8s', delay: '-1.2s' },
  { top: '7%',  left: '94%', size: 16, color: '#ffb866', dur: '3.6s', delay: '-0.5s' },
  { top: '23%', left: '88%', size: 12, color: '#fff2c0', dur: '5.4s', delay: '-3s'   },
]

interface TitleScreenBProps {
  /** See TitleScreenA — `'multi'` mounts the twin-scrolls overlay
   *  from the first frame; `'menu'` (default) starts on the regular
   *  title menu. App.tsx passes `'multi'` when the URL is `?multi`
   *  and the multi store's phase is `hub`. */
  initialMode?: 'menu' | 'multi'
}

export function TitleScreenB({ initialMode = 'menu' }: TitleScreenBProps = {}): ReactElement {
  const h = useTitleHandlers()
  const isMobile = useIsMobile()
  const foldOpen = useIsFoldOpen()
  const [isFs, setIsFs] = useState(
    typeof document !== 'undefined' && !!document.fullscreenElement,
  )
  const [multiMode, setMultiMode] = useState(initialMode === 'multi')
  const closeMulti = () => setMultiMode(false)

  useEffect(() => {
    const onChange = () => setIsFs(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  // Persist current variant so / resolves here on the next visit.
  useEffect(() => {
    try { window.localStorage.setItem('origami-theme', 'b') } catch { /* ignore */ }
  }, [])

  // ESC closes the multi overlay; AnimatePresence handles the exit
  // animation and then navigates back to the title URL.
  useEffect(() => {
    if (!multiMode) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeMulti() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [multiMode])

  const navigateBackToMenu = () => {
    window.history.pushState(null, '', '?')
    window.dispatchEvent(new Event('url-changed'))
  }

  function toggleFullscreen() {
    if (document.fullscreenElement) {
      void document.exitFullscreen()
    } else {
      void document.documentElement.requestFullscreen().catch(() => {})
    }
  }

  function switchTheme(target: 'a' | 'b') {
    return (e: React.MouseEvent) => {
      e.preventDefault()
      try { window.localStorage.setItem('origami-theme', target) } catch { /* ignore */ }
      window.history.pushState(null, '', `?title=${target}`)
      window.dispatchEvent(new Event('url-changed'))
    }
  }

  function goToSettings(e: React.MouseEvent) {
    e.preventDefault()
    window.history.pushState(null, '', '?settings')
    window.dispatchEvent(new Event('url-changed'))
  }

  function handleClick(id: typeof MENU[number]['id']) {
    switch (id) {
      case 'solo':  return h.hasActiveRun === true ? h.continueRun() : h.startSolo()
      case 'codex': return h.openCompendium()
      case 'lobby': return h.openMultiplayer()
      case 'join':
        if (!h.joinExpanded) return h.expandJoin()
        return h.submitJoin()
    }
  }

  // When the player has an active run, the solo CTA shifts from
  // "Begin Solo Run" to "Continue Run" and an "or start new run"
  // link appears beneath it.
  function resolveItem(item: typeof MENU[number]) {
    if (item.id === 'solo' && h.hasActiveRun === true) {
      return { ...item, label: 'Continue Run', sub: 'resume your folded five' }
    }
    return item
  }

  return (
    <div className={`tsB${multiMode ? ' tsB--multi' : ''}`}>
      <TitleToast message={h.toast} tone="twilight" />

      {h.confirmingNewRun && (
        <div
          className="tsB__confirm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tsB-confirm-title"
          onClick={h.cancelNewRun}
        >
          <div className="tsB__confirm-card" onClick={(e) => e.stopPropagation()}>
            <h2 id="tsB-confirm-title" className="tsB__confirm-title">
              Start a new run?
            </h2>
            <p className="tsB__confirm-body">
              Your current run will be lost. You'll pick a new mascot,
              name, and team.
            </p>
            <div className="tsB__confirm-actions">
              <button
                type="button"
                className="tsB__confirm-btn tsB__confirm-btn--ghost"
                onClick={h.cancelNewRun}
              >
                Cancel
              </button>
              <button
                type="button"
                className="tsB__confirm-btn tsB__confirm-btn--primary"
                onClick={h.confirmNewRun}
              >
                Start new run
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="tsB__wall" aria-hidden="true" />

      {isMobile ? (
        <MobileTitleB
          h={h}
          isFs={isFs}
          toggleFullscreen={toggleFullscreen}
          switchTheme={switchTheme}
          goToSettings={goToSettings}
          handleClick={handleClick}
        />
      ) : (
      <>
      {/* Title block — hanko stamp + Origami Arena wordmark, mirroring
          Variant A's identity. Cream typography against the twilight wall. */}
      <header className="tsB__title-block">
        <div className="tsB__hanko" aria-hidden="true">
          <span className="tsB__hanko-kanji">折</span>
          <span className="tsB__hanko-kanji">鶴</span>
          <span className="tsB__hanko-wear tsB__hanko-wear--tl" />
          <span className="tsB__hanko-wear tsB__hanko-wear--br" />
        </div>
        <div className="tsB__title-text">
          <h1 className="tsB__roman">Origami Arena</h1>
          <p className="tsB__tagline">
            <em>Fold and evolve an army of tiny paper friends!</em>
          </p>
        </div>
      </header>

      {/* Centered menu — dark twilight tablets, lit by lantern-gold
          accents. */}
      <nav className="tsB__menu-center" aria-label="Main menu">
        {MENU.map((raw) => {
          const item = resolveItem(raw)
          const expanded = item.id === 'join' && h.joinExpanded
          const showNewLink = item.id === 'solo' && h.hasActiveRun === true
          return (
            <div
              key={item.id}
              className={`tsB__tablet${expanded ? ' tsB__tablet--expanded' : ''}`}
            >
              <button
                type="button"
                className="tsB__tablet-btn"
                onClick={() => handleClick(item.id)}
              >
                <span className="tsB__lantern-dot" aria-hidden="true" />
                <span className="tsB__tablet-body">
                  <span className="tsB__tablet-label">{item.label}</span>
                  <span className="tsB__tablet-sub">{item.sub}</span>
                </span>
                <span className="tsB__tablet-arrow" aria-hidden="true">▸</span>
              </button>
              {showNewLink && (
                <button
                  type="button"
                  className="tsB__solo-newlink"
                  onClick={h.requestNewRun}
                >
                  or start a new run
                </button>
              )}
              {expanded ? (
                <div className="tsB__join">
                  <input
                    ref={h.joinInputRef}
                    className="tsB__join-input"
                    type="text"
                    maxLength={6}
                    value={h.joinCode}
                    onChange={(e) =>
                      h.setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') h.submitJoin()
                      if (e.key === 'Escape') h.collapseJoin()
                    }}
                    placeholder="ABCD12"
                    aria-label="6-letter lobby code"
                  />
                  <button
                    type="button"
                    className="tsB__join-go"
                    onClick={h.submitJoin}
                  >
                    Go →
                  </button>
                </div>
              ) : null}
            </div>
          )
        })}
      </nav>

      {/* Star field — twinkles in the painted night sky only,
          kept off the centered title-block area. */}
      <div className="tsB__particles" aria-hidden="true">
        {STARS.map((s, i) => (
          <div
            key={i}
            className="tsB__star-slot"
            style={{ top: s.top, left: s.left }}
          >
            <Sparkle
              size={s.size}
              color={s.color}
              style={{ animation: `starTwinkle ${s.dur} ease-in-out ${s.delay} infinite` }}
            />
          </div>
        ))}
      </div>

      <footer className="tsB__footer">
        <span className="tsB__footer-left">
          <span className="tsB__version">v 0.1</span>
          <button
            type="button"
            className="tsB__fs-btn"
            onClick={toggleFullscreen}
            aria-pressed={isFs}
          >
            <span className="tsB__fs-glyph" aria-hidden="true">
              {isFs ? '⤡' : '⤢'}
            </span>
            <span>
              {foldOpen
                ? isFs ? 'Exit' : 'Fullscreen'
                : isFs ? 'Exit Full Screen' : 'View Full Screen'}
            </span>
          </button>
        </span>
        <span className="tsB__cue">
          <span className="tsB__cue-group">
            <span>{foldOpen ? '5 animals' : 'five animals'}</span>
            <span className="tsB__cue-chips" aria-hidden="true">
              {ANIMALS.map((a) => (
                <span
                  key={a}
                  className="tsB__cue-chip"
                  style={{ background: ANIMAL_INFO[a].bg }}
                  title={ANIMAL_INFO[a].en}
                >
                  {ANIMAL_INFO[a].kanji}
                </span>
              ))}
            </span>
          </span>
          <span className="tsB__cue-sep" aria-hidden="true">·</span>
          <span className="tsB__cue-group">
            <span>{foldOpen ? '5 elements' : 'five elements'}</span>
            <span className="tsB__cue-chips" aria-hidden="true">
              {ELEMENTS.map((e) => (
                <span
                  key={e}
                  className="tsB__cue-chip"
                  style={{
                    background: ELEMENT_INFO[e].bg,
                    color: ELEMENT_INFO[e].fg,
                  }}
                  title={ELEMENT_INFO[e].en}
                >
                  {ELEMENT_INFO[e].kanji}
                </span>
              ))}
            </span>
          </span>
          <span className="tsB__cue-sep" aria-hidden="true">·</span>
          <span>{foldOpen ? '25 mins' : 'twenty-five minutes to play'}</span>
        </span>
        <span className="tsB__footer-right">
          <span className="tsB__health">
            <span
              className={`tsB__dot tsB__dot--${h.health.phase}`}
              aria-hidden="true"
            />
            <span>
              {h.health.phase === 'connecting'
                ? 'connecting…'
                : h.health.phase === 'online'
                  ? `online · ${h.health.units} units`
                  : 'offline'}
            </span>
          </span>
          <span className="tsB__theme" role="group" aria-label="Theme">
            <a
              href="?title=a"
              onClick={switchTheme('a')}
              className="tsB__theme-opt"
              title="Light theme"
            >
              <span aria-hidden="true">☀</span>
            </a>
            <a
              href="?title=b"
              onClick={switchTheme('b')}
              className="tsB__theme-opt tsB__theme-opt--active"
              title="Dark theme"
              aria-current="page"
            >
              <span aria-hidden="true">☾</span>
            </a>
          </span>
          <a
            href="?settings"
            onClick={goToSettings}
            className="tsB__cog"
            title="Settings"
            aria-label="Settings"
          >
            <img
              className="tsB__cog-icon"
              src="/ui-assets/settings-gear.webp"
              alt=""
              aria-hidden="true"
            />
          </a>
        </span>
      </footer>
      </>
      )}

      {/* Multi-entry overlay. Mirrors TitleScreenA — twin scrolls
          unroll over the menu when in multi mode, exit animation
          plays before route change so the transition feels smooth. */}
      <AnimatePresence onExitComplete={navigateBackToMenu}>
        {multiMode && (
          <div className="tsB__multi-overlay" key="multi-overlay">
            <button
              type="button"
              className="tsB__multi-close"
              onClick={closeMulti}
              aria-label="Close multiplayer"
              title="Back to title"
            >
              <span aria-hidden>‹</span>
              <span className="tsB__multi-close-label">Back</span>
            </button>
            <MultiTwinScrolls compact={isMobile} />
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

/* ============================================================
   Mobile fork — LANDSCAPE-locked. Portrait phones get the
   <RotatePrompt /> overlay; landscape phones get a centered
   composition with star field above the title + menu. */
interface MobileProps {
  h: ReturnType<typeof useTitleHandlers>
  isFs: boolean
  toggleFullscreen: () => void
  switchTheme: (target: 'a' | 'b') => (e: React.MouseEvent) => void
  goToSettings: (e: React.MouseEvent) => void
  handleClick: (id: typeof MENU[number]['id']) => void
}

function MobileTitleB({ h, isFs, toggleFullscreen, switchTheme, goToSettings, handleClick }: MobileProps): ReactElement {
  function resolveItem(item: typeof MENU[number]) {
    if (item.id === 'solo' && h.hasActiveRun === true) {
      return { ...item, label: 'Continue Run', sub: 'resume your folded five' }
    }
    return item
  }
  return (
    <>
      <RotatePrompt />

      <div className="tsB__particles" aria-hidden="true">
        {STARS_LANDSCAPE.map((s, i) => (
          <div
            key={i}
            className="tsB__star-slot"
            style={{ top: s.top, left: s.left }}
          >
            <Sparkle
              size={s.size}
              color={s.color}
              style={{ animation: `starTwinkle ${s.dur} ease-in-out ${s.delay} infinite` }}
            />
          </div>
        ))}
      </div>

      <main className="tsB-m__stage">
        <header className="tsB-m__title">
          <div className="tsB__hanko" aria-hidden="true">
            <span className="tsB__hanko-kanji">折</span>
            <span className="tsB__hanko-kanji">鶴</span>
            <span className="tsB__hanko-wear tsB__hanko-wear--tl" />
            <span className="tsB__hanko-wear tsB__hanko-wear--br" />
          </div>
          <div className="tsB-m__title-text">
            <h1 className="tsB-m__roman">Origami Arena</h1>
            <p className="tsB-m__tagline">
              <em>Fold and evolve an army of tiny paper friends!</em>
            </p>
          </div>
        </header>

        <nav className="tsB-m__menu" aria-label="Main menu">
          {MENU.map((raw) => {
            const item = resolveItem(raw)
            const expanded = item.id === 'join' && h.joinExpanded
            const showNewLink = item.id === 'solo' && h.hasActiveRun === true
            return (
              <div
                key={item.id}
                className={`tsB__tablet${expanded ? ' tsB__tablet--expanded' : ''}`}
              >
                <button
                  type="button"
                  className="tsB__tablet-btn"
                  onClick={() => handleClick(item.id)}
                >
                  <span className="tsB__lantern-dot" aria-hidden="true" />
                  <span className="tsB__tablet-body">
                    <span className="tsB__tablet-label">{item.label}</span>
                    <span className="tsB__tablet-sub">{item.sub}</span>
                  </span>
                  <span className="tsB__tablet-arrow" aria-hidden="true">▸</span>
                </button>
                {showNewLink && (
                  <button
                    type="button"
                    className="tsB__solo-newlink"
                    onClick={h.requestNewRun}
                  >
                    or start a new run
                  </button>
                )}
                {expanded ? (
                  <div className="tsB__join">
                    <input
                      ref={h.joinInputRef}
                      className="tsB__join-input"
                      type="text"
                      maxLength={6}
                      value={h.joinCode}
                      onChange={(e) =>
                        h.setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') h.submitJoin()
                        if (e.key === 'Escape') h.collapseJoin()
                      }}
                      placeholder="ABCD12"
                      aria-label="6-letter lobby code"
                    />
                    <button
                      type="button"
                      className="tsB__join-go"
                      onClick={h.submitJoin}
                    >
                      Go →
                    </button>
                  </div>
                ) : null}
              </div>
            )
          })}
        </nav>
      </main>

      <footer className="tsB-m__footer">
        <span className="tsB-m__footer-left">
          <button
            type="button"
            className="tsB__fs-btn"
            onClick={toggleFullscreen}
            aria-pressed={isFs}
          >
            <span className="tsB__fs-glyph" aria-hidden="true">
              {isFs ? '⤡' : '⤢'}
            </span>
            <span>{isFs ? 'Exit' : 'Fullscreen'}</span>
          </button>
          <span className="tsB__version">v 0.1</span>
        </span>
        <span className="tsB-m__footer-right">
          <span className="tsB__health">
            <span
              className={`tsB__dot tsB__dot--${h.health.phase}`}
              aria-hidden="true"
            />
            <span>
              {h.health.phase === 'connecting'
                ? 'connecting…'
                : h.health.phase === 'online'
                  ? `online · ${h.health.units} units`
                  : 'offline'}
            </span>
          </span>
          <span className="tsB__theme" role="group" aria-label="Theme">
            <a
              href="?title=a"
              onClick={switchTheme('a')}
              className="tsB__theme-opt"
              title="Light theme"
            >
              <span aria-hidden="true">☀</span>
            </a>
            <a
              href="?title=b"
              onClick={switchTheme('b')}
              className="tsB__theme-opt tsB__theme-opt--active"
              title="Dark theme"
              aria-current="page"
            >
              <span aria-hidden="true">☾</span>
            </a>
          </span>
          <a
            href="?settings"
            onClick={goToSettings}
            className="tsB__cog"
            title="Settings"
            aria-label="Settings"
          >
            <img
              className="tsB__cog-icon"
              src="/ui-assets/settings-gear.webp"
              alt=""
              aria-hidden="true"
            />
          </a>
        </span>
      </footer>
    </>
  )
}
